import { PreLabelStyles } from './../../../shared/components/feature-tile/feature-tile-variants';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import {
  ConsolidatedFeature,
  ConsolidatorDevice,
  EssBatteryStateProperties,
  EssOperationStateProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
} from '@vi-iot/client';

import { DevicesStore, FeatureService, InstallationFeaturesStore } from '@store';

import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';
import { CustomTranslationsService } from 'src/app/operational-data-common/translations';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-battery',
  templateUrl: './battery.component.html',
  styleUrls: ['./battery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatteryComponent implements OnInit, OnDestroy {
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  readonly featurePropertyKey = FeaturePropertyKey;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  batteryOperationState$: Observable<Feature<EssOperationStateProperties, never>>;
  batteryState$: Observable<Feature<EssBatteryStateProperties, never>>;
  power$: Observable<Feature<IsNumberValueProperty, unknown>>;
  powerConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  powerConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  powerConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  stateOfCharge$: Observable<Feature<IsNumberValueProperty, unknown>>;
  stateOfChargeConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  stateOfChargeConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  stateOfChargeConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  exceptionsState: string;
  basicState: string;
  stateOfChargeConsolidatedDeviceId: string;
  powerConsolidatedDeviceId: string;
  preLabelStyles: PreLabelStyles = {
    fontSize: '18px',
    fontWeight: '500',
  };
  @Input() deviceSelectId: string;
  @Input() deviceId: string;

  constructor(
    public devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private customTranslationsService: CustomTranslationsService,
    private translateService: TranslateService,
    private featureService: FeatureService
  ) {}

  ngOnInit(): void {
    this.featureService
      .getConsolidatedFeatures(FeatureNames.stateOfChargeConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.stateOfChargeConsolidatedDeviceId = feature?.deviceId;
      });
    this.featureService
      .getConsolidatedFeatures(FeatureNames.powerConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature) => {
        this.powerConsolidatedDeviceId = feature?.deviceId;
      });

    this.batteryOperationState$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteryOperationState,
      this.deviceId
    );
    this.power$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.power,
      this.deviceId
    );
    this.powerConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsolidated,
      this.deviceId
    );

    this.powerConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.powerConsolidated
    );

    this.powerConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.powerConsolidated,
      this.deviceId,
      this.powerConsolidatedDeviceId
    );

    this.stateOfCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.stateOfCharge,
      this.deviceId
    );
    this.stateOfChargeConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.stateOfChargeConsolidated,
      this.deviceId
    );

    this.stateOfChargeConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.stateOfChargeConsolidated
    );

    this.stateOfChargeConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.stateOfChargeConsolidated,
      this.deviceId,
      this.stateOfChargeConsolidatedDeviceId
    );

    this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.batteryState, this.deviceId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((feature: Feature<EssBatteryStateProperties, never>) => {
        if (feature?.properties) {
          this.batteryStateValues(feature);
        }
      });
  }

  batteryStateValues(feature: Feature<EssBatteryStateProperties, never>): void {
    const state = (feature.properties?.exception?.value)
      .map((value) => {
        const param = this.customTranslationsService.ESS_STATES_EXCEPTION[value] ?? value;

        return this.translateService.instant(param);
      })
      .join(', ');
    this.exceptionsState = state ? '(' + state + ')' : '';

    this.basicState = (feature.properties?.basic?.value)
      .map((value) => {
        const param = this.customTranslationsService.ESS_STATES_BASIC[value] ?? value;

        return this.translateService.instant(param);
      })
      .join(', ');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
